/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { SoundManager } from '../game/SoundManager';

interface LandingScreenProps {
  onStart: () => void;
  externalMusicOn?: boolean;
  setExternalMusicOn?: (value: boolean) => void;
  onMemeTest: () => void;
  // Add these new props
  isFromPortal?: boolean;
  portalRefUrl?: string;
}

const StartButton = styled.button`
  padding: 5px 22px;
  font-size: 1rem;
  background: #228B22; /* Darker green */
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Impact', sans-serif;
  letter-spacing: 1px;
  text-shadow: 0 0 5px #228B22, 0 0 10px #228B22, 0 0 15px #228B22, 0 0 20px #228B22;
  box-shadow: 0 0 5px #228B22, 0 0 10px #228B22, 0 0 15px #228B22, 0 0 20px #228B22;
  transition: transform 0.3s ease-in-out;
  
  &:hover {
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    padding: 8px 30px;
    font-size: 1rem;
  }
`;

const MusicButton = styled.button<{ musicOn: boolean }>`
  padding: 6px 12px;
  font-size: 0.8rem;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  background: ${({ musicOn }) => (musicOn ? '#228B22' : '#444')};
  font-family: Arial, sans-serif;
  transition: background 0.3s ease-in-out;
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.94);
  font-size: 0.92rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.75);
  margin: 1rem 0 0 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  
  @media (max-width: 768px) {
    position: absolute;
    bottom: 20px;
    left: 20px;
    margin: 0;
    text-align: left;
    gap: 0.25rem;
  }
`;

const StyledLink = styled.a`
  color: #f5f7ff;
  text-decoration: none;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.45);
  padding-bottom: 1px;
  transition: color 0.2s, border-color 0.2s;
  
  &:hover {
    color: #9be37d;
    border-color: rgba(155, 227, 125, 0.8);
  }
`;

const TaglineLabel = styled.span`
  opacity: 0.82;
`;

const TaglineSuffix = styled.span`
  opacity: 0.9;
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(/landing2b.png);
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: 20px;
  
  @media (max-width: 768px) {
    background-image: url(/landing2b.png);
    background-size: cover;
    background-position: center;
    background-color: #000; /* Optional blending tone */
    justify-content: center;
    padding: 20px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  transform: translate(52px, -70px); /* Desktop: keep original position */
  
  @media (max-width: 768px) {
    /* Override for mobile: position buttons at Pepe's hand (bottom left) */
    position: absolute;
    bottom: 510px;
    left: 20px;
    transform: none;
    margin-bottom: 0;
  }
`;

// Mobile wrapper (only used for mobile layout)
const MobileContent = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 100%; /* So the absolutely positioned elements work properly */
  }
`;

// Desktop wrapper (unchanged)
const DesktopContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ReturnPortalButton = styled.button`
  padding: 5px 22px;
  font-size: 1rem;
  background: #8B22BB; /* Purple for portal */
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Impact', sans-serif;
  letter-spacing: 1px;
  text-shadow: 0 0 5px #8B22BB, 0 0 10px #8B22BB, 0 0 15px #8B22BB, 0 0 20px #8B22BB;
  box-shadow: 0 0 5px #8B22BB, 0 0 10px #8B22BB, 0 0 15px #8B22BB, 0 0 20px #8B22BB;
  transition: transform 0.3s ease-in-out;
  margin-top: 8px;
  
  &:hover {
    transform: scale(1.1);
  }
`;

// Mobile logo at top (unchanged)
const MobileLogoContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-top: 5px; /* small top spacing */
  }
`;

const MobileLogoText = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(255, 128, 0, 0.8), 0 0 20px rgba(255, 128, 0, 0.6);
  margin: 0 10px;
  letter-spacing: 2px;
`;

const LandingScreen: React.FC<LandingScreenProps> = ({
  onStart,
  externalMusicOn,
  setExternalMusicOn,
  onMemeTest,
  isFromPortal = false,
  portalRefUrl = '',
}) => {
  const [internalMusicOn, setInternalMusicOn] = useState(false);
  const musicOn = externalMusicOn !== undefined ? externalMusicOn : internalMusicOn;
  const setMusicOn = setExternalMusicOn || setInternalMusicOn;

  useEffect(() => {
    SoundManager.preloadSounds();
    return () => {
      if (setExternalMusicOn === undefined && musicOn) {
        SoundManager.stopSound('music');
      }
    };
  }, []);

  useEffect(() => {
    if (musicOn) {
      SoundManager.playSound('music', 0.5, true);
      return;
    }

    SoundManager.stopSound('music');
  }, [musicOn]);

  const handleReturnToPortal = () => {
    if (portalRefUrl) {
      // Format the URL properly
      let returnUrl = portalRefUrl;
      if (!returnUrl.startsWith('http://') && !returnUrl.startsWith('https://')) {
        returnUrl = 'https://' + returnUrl;
      }
      
      // Navigate back to the referring game
      window.location.href = returnUrl;
    }
  };

  const toggleMusic = () => {
    setMusicOn(!musicOn);
  };

  return (
    <Container>
      {/* Desktop version (unchanged) */}
      <DesktopContent>
      <ButtonGroup>
    <StartButton onClick={onStart}>Start</StartButton>
    {isFromPortal && portalRefUrl && (
      <ReturnPortalButton onClick={handleReturnToPortal}>
        Return to Portal
      </ReturnPortalButton>
    )}
    <MusicButton onClick={toggleMusic} musicOn={musicOn}>
      {musicOn ? 'Music: On' : 'Music: Off'}
    </MusicButton>
  </ButtonGroup>
        <Tagline>
          <TaglineLabel>A</TaglineLabel>
          <StyledLink href="https://www.createa.meme/" target="_blank" rel="noopener noreferrer">
            Create a Meme
          </StyledLink>{' '}
          <TaglineSuffix>Video Game</TaglineSuffix>
        </Tagline>
      </DesktopContent>
      
      {/* Mobile version */}
      <MobileContent>
        <MobileLogoContainer>
          <MobileLogoText>DANK</MobileLogoText>
          <MobileLogoText>DUEL</MobileLogoText>
        </MobileLogoContainer>
        
        <ButtonGroup>
          <StartButton onClick={onStart}>Start</StartButton>
          <MusicButton onClick={toggleMusic} musicOn={musicOn}>
            {musicOn ? 'Music: On' : 'Music: Off'}
          </MusicButton>
        </ButtonGroup>
        
        <Tagline>
          <TaglineLabel>A</TaglineLabel>
          <StyledLink href="https://www.createa.meme/" target="_blank" rel="noopener noreferrer">
            Create a Meme
          </StyledLink>{' '}
          <TaglineSuffix>Video Game</TaglineSuffix>
        </Tagline>
      </MobileContent>
    </Container>
  );
};

export default LandingScreen;
